import React, { useMemo, useState } from 'react';
import {
  App,
  Button,
  Card,
  Col,
  Modal,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  Typography,
  Upload,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  DownloadOutlined,
  EditOutlined,
  EyeOutlined,
  FileExcelOutlined,
  PlusOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import hrmsService, { hrmsKeys } from '@/services/hrms.service';
import { useEmployeeOptions } from './shared/useEmployeeOptions';
import { hrmsStatusColor } from './shared/hrmsStatus';

const { Text } = Typography;

function saveBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

const SalaryRevisionsPage: React.FC = () => {
  const navigate = useNavigate();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [employeeId, setEmployeeId] = useState<string>();
  const [statusFilter, setStatusFilter] = useState<string>();
  const [excelBusy, setExcelBusy] = useState(false);
  const [importErrors, setImportErrors] = useState<string[]>([]);
  const { options: empOptions, empLabel } = useEmployeeOptions();

  const listParams = useMemo(
    () => ({ employee_id: employeeId || undefined }),
    [employeeId]
  );

  const { data: rows = [], isLoading } = useQuery({
    queryKey: hrmsKeys.salaryRevisions(listParams),
    queryFn: () => hrmsService.listSalaryRevisions(listParams),
  });

  const filtered = useMemo(
    () => (statusFilter ? rows.filter((r: any) => r.status === statusFilter) : rows),
    [rows, statusFilter]
  );

  const stats = useMemo(() => {
    const pending = rows.filter(
      (r: any) => r.status === 'Draft' || r.status === 'Pending' || r.status === 'PendingApproval'
    ).length;
    const applied = rows.filter((r: any) => r.status === 'Applied' || r.status === 'Approved').length;
    return { total: rows.length, pending, applied };
  }, [rows]);

  const handleTemplate = async () => {
    setExcelBusy(true);
    try {
      saveBlob(
        await hrmsService.downloadSalaryRevisionTemplateBlob(),
        'salary-revisions-import-template.xlsx'
      );
      message.success('Template downloaded');
    } catch (e: any) {
      message.error(e?.response?.data?.message || e?.message || 'Template download failed');
    } finally {
      setExcelBusy(false);
    }
  };

  const handleExport = async () => {
    setExcelBusy(true);
    try {
      saveBlob(
        await hrmsService.exportSalaryRevisionsBlob({
          employee_id: employeeId || undefined,
          status: statusFilter || undefined,
        }),
        `salary-revisions-${dayjs().format('YYYY-MM-DD')}.xlsx`
      );
      message.success('Export downloaded');
    } catch (e: any) {
      message.error(e?.response?.data?.message || e?.message || 'Export failed');
    } finally {
      setExcelBusy(false);
    }
  };

  const handleImportFile = async (file: File) => {
    setExcelBusy(true);
    setImportErrors([]);
    try {
      const result = await hrmsService.importSalaryRevisionsExcel(file);
      if (result?.errors?.length) {
        setImportErrors(result.errors);
        message.error(`Import failed — ${result.errors.length} problem(s), nothing applied`);
      } else {
        message.success(
          `Import finished: ${result.created} revision(s) created and submitted for approval`
        );
        await queryClient.invalidateQueries({ queryKey: hrmsKeys.salaryRevisions() });
      }
    } catch (e: any) {
      const errors: string[] | undefined = e?.response?.data?.data?.errors;
      if (errors?.length) setImportErrors(errors);
      else message.error(e?.response?.data?.message || e?.message || 'Import failed');
    } finally {
      setExcelBusy(false);
    }
  };

  const columns: ColumnsType<any> = [
    {
      title: 'Revision #',
      dataIndex: 'revision_number',
      width: 140,
      fixed: 'left',
      render: (v: string, r) => (
        <a onClick={() => navigate(`/hr/salary-revisions/${r.id}`)}>{v || 'Open'}</a>
      ),
    },
    { title: 'Employee', dataIndex: 'employee_id', ellipsis: true, render: (id: string) => empLabel(id) },
    { title: 'Type', dataIndex: 'revision_type', width: 120 },
    {
      title: 'Effective',
      dataIndex: 'effective_from',
      width: 120,
      render: (d: string) => (d ? dayjs(d).format('DD-MMM-YYYY') : '—'),
    },
    { title: 'Previous basic', dataIndex: 'previous_basic', width: 120 },
    { title: 'New basic', dataIndex: 'new_basic', width: 110 },
    {
      title: 'Heads',
      key: 'heads',
      width: 80,
      render: (_, r) => (Array.isArray(r.lines_json) ? r.lines_json.length : '—'),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 110,
      render: (s: string) => <Tag color={hrmsStatusColor(s)}>{s}</Tag>,
    },
    {
      title: 'Actions',
      key: 'a',
      fixed: 'right',
      width: 140,
      render: (_, r) => (
        <Space size={0}>
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/hr/salary-revisions/${r.id}`);
            }}
          >
            View
          </Button>
          {r.status === 'Draft' && (
            <Button
              type="link"
              size="small"
              icon={<EditOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/hr/salary-revisions/${r.id}/edit`);
              }}
            >
              Edit
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <WorkspaceLayout
      fullBleed
      header={
        <EnterprisePageHeader
          documentLabel="Salary revisions"
          subtitle="Promote salary-head / grade changes via approval"
          primaryActions={
            <Space wrap>
              <Button icon={<FileExcelOutlined />} loading={excelBusy} onClick={handleTemplate}>
                Template
              </Button>
              <Upload
                accept=".xlsx,.xls"
                showUploadList={false}
                beforeUpload={(file) => {
                  void handleImportFile(file);
                  return false;
                }}
              >
                <Button icon={<UploadOutlined />} loading={excelBusy}>
                  Import
                </Button>
              </Upload>
              <Button icon={<DownloadOutlined />} loading={excelBusy} onClick={handleExport}>
                Export
              </Button>
              <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/hr/salary-revisions/new')}>
                New revision
              </Button>
            </Space>
          }
        />
      }
    >
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={12} sm={8}><Card size="small"><Statistic title="Total" value={stats.total} /></Card></Col>
        <Col xs={12} sm={8}><Card size="small"><Statistic title="Draft / Pending" value={stats.pending} /></Card></Col>
        <Col xs={24} sm={8}><Card size="small"><Statistic title="Approved / Applied" value={stats.applied} /></Card></Col>
      </Row>
      <Card size="small" style={{ marginBottom: 12 }}>
        <Space wrap>
          <Select
            allowClear
            placeholder="Status"
            style={{ width: 160 }}
            value={statusFilter}
            onChange={setStatusFilter}
            options={['Draft', 'Pending', 'PendingApproval', 'Approved', 'Applied', 'Rejected'].map((v) => ({
              value: v,
              label: v,
            }))}
          />
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder="Employee"
            style={{ width: 280 }}
            value={employeeId}
            onChange={setEmployeeId}
            options={empOptions}
          />
          <Text type="secondary">{filtered.length} record(s)</Text>
        </Space>
      </Card>
      <Table
        rowKey="id"
        loading={isLoading}
        columns={columns}
        dataSource={filtered}
        scroll={{ x: 1000 }}
        pagination={{ pageSize: 20 }}
        onRow={(r) => ({
          onClick: () => navigate(`/hr/salary-revisions/${r.id}`),
          style: { cursor: 'pointer' },
        })}
      />
      <Modal
        open={importErrors.length > 0}
        title="Import failed — nothing was imported"
        onCancel={() => setImportErrors([])}
        footer={<Button onClick={() => setImportErrors([])}>Close</Button>}
        width={640}
      >
        <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
          Fix the rows below and upload again.
        </Text>
        <pre style={{ maxHeight: 360, overflow: 'auto', whiteSpace: 'pre-wrap', margin: 0 }}>
          {importErrors.slice(0, 100).join('\n')}
          {importErrors.length > 100 ? `\n…and ${importErrors.length - 100} more` : ''}
        </pre>
      </Modal>
    </WorkspaceLayout>
  );
};

export default SalaryRevisionsPage;
